/**
 * @file useCardHighlight.ts
 * @description Контур подложки секции на канве (`space-highlight { part:
 * "card" }`, замечание владельца 2026-10-19; ревью малой партии Н1, М1; QA).
 *
 * - Контур стоит, пока курсор над настройками подложки **или** фокус внутри
 *   них: два флага, `set("hover" | "focus", on)`. Ушёл курсор, а фокус
 *   остался на флажке или ползунке — контур держится; фокус ушёл наружу,
 *   а курсор над настройками — тоже держится. Снимается, когда нет обоих.
 * - Размонтирование панели (ссылка «Оформление → Цвета →», закрытие шторки
 *   на 360, другой блок — новый `highlight`) снимает контур, если он стоит:
 *   иначе он остался бы на канве навсегда.
 * - Смена `key` (включили подложку, сменили ширину) при стоящем контуре —
 *   повторная отправка после черновика блока: канва меряет `.sec-card` в
 *   обработчике сообщения, а черновик уходит через `LIVE_DELAY_MS` и ещё
 *   рисуется — без повтора контура нет или ярлык остаётся на старом месте.
 */

import { useCallback, useEffect, useRef } from "react";

import type { SpaceHighlightPart } from "../../lib/site-view/canvas-messages";
import { LIVE_DELAY_MS } from "./useSectionDraft";

/** Повтор контура: после отправки черновика (`LIVE_DELAY_MS`) и его отрисовки на канве. */
export const CARD_HIGHLIGHT_RESEND_MS = LIVE_DELAY_MS + 150;

/** Что держит контур: курсор над настройками или фокус внутри них. */
export type CardHighlightSource = "hover" | "focus";

export function useCardHighlight(
  highlight: (part: SpaceHighlightPart | null) => void,
  key: string,
): (source: CardHighlightSource, on: boolean) => void {
  const sources = useRef({ hover: false, focus: false });
  // Отправлено ли канве «card» (без повторов на каждое событие).
  const active = useRef(false);

  const set = useCallback(
    (source: CardHighlightSource, on: boolean) => {
      sources.current[source] = on;
      const want = sources.current.hover || sources.current.focus;
      if (want === active.current) return;
      active.current = want;
      highlight(want ? "card" : null);
    },
    [highlight],
  );

  // Размонтирование или другой блок: снять контур со «своего» блока.
  useEffect(
    () => () => {
      sources.current = { hover: false, focus: false };
      if (!active.current) return;
      active.current = false;
      highlight(null);
    },
    [highlight],
  );

  // Подложка появилась или сдвинулась — перемерить на канве после черновика.
  useEffect(() => {
    if (!active.current) return;
    const t = setTimeout(() => {
      if (active.current) highlight("card");
    }, CARD_HIGHLIGHT_RESEND_MS);
    return () => clearTimeout(t);
  }, [highlight, key]);

  return set;
}
